import type { ADDON_NAMES, PlanIDs } from '@proton/payments';
import { AddonKey, AddonLimit, type Cycle, type Plan } from '@proton/payments';
import { getSupportedAddons } from '@proton/payments';
import { getAddonMultiplier, getMaxValue, getPlanMaxIPs } from '@proton/payments';
import isTruthy from '@proton/utils/isTruthy';

export default function getAddonsPricing({
    currentPlan,
    plansMap,
    planIDs,
    cycle,
}: {
    currentPlan: Plan;
    plansMap: { [key: string]: Plan };
    planIDs: PlanIDs;
    cycle: Cycle;
}) {
    const supportedAddons = getSupportedAddons(planIDs);

    return Object.entries(supportedAddons)
        .map(([addonName]) => {
            const addon = plansMap[addonName];

            if (!addon) {
                return;
            }

            const addonNameKey = addon.Name as ADDON_NAMES;
            const quantity = planIDs[addon.Name] ?? 0;

            const isSupported = !!supportedAddons[addonNameKey];
            const addonMaxKey = AddonKey[addonNameKey];

            const addonMultiplier = getAddonMultiplier(addonMaxKey, addon);

            // The same workaround as above
            const min: number = getMaxValue(currentPlan, addonMaxKey);

            const max = AddonLimit[addonNameKey] * addonMultiplier;
            // Member addon comes with MaxSpace + MaxAddresses
            const value = isSupported
                ? min + quantity * addonMultiplier
                : Object.entries(planIDs).reduce((acc, [planName, quantity]) => {
                      // and the same workaround as above
                      let multiplier: number;
                      if (addonMaxKey === 'MaxIPs') {
                          multiplier = getPlanMaxIPs(plansMap[planName]);
                      } else {
                          multiplier = getMaxValue(plansMap[planName], addonMaxKey);
                      }

                      return acc + quantity * multiplier;
                  }, 0);

            const addonPricePerCycle = addon.Pricing[cycle] || 0;

            if (!value) {
                return;
            }

            return {
                addonPricePerCycle,
                cycle,
                value,
                min,
                max,
                addon,
                isSupported,
                addonMultiplier,
            };
        })
        .filter(isTruthy);
}
